import React, { useState, useEffect } from 'react';
// Still imported: this global file defines generic class names (.btn, .order-table,
// .delivered, …) that apply across the app. This page itself is styled by
// OrderView.module.css.
import './OrderView.css';
import styles from './OrderView.module.css';
import { API_BASE_URL } from '../Config.js';
import {
    BookOpen,
    ChevronDown,
    CircleX,
    Clock,
    IndianRupee,
    Inbox,
    PackageCheck,
    Receipt,
    RefreshCw,
    Wallet,
    ClipboardList,
} from 'lucide-react';

const STATUS_TABS = [
    { value: 'PENDING', label: 'Pending', icon: Clock },
    { value: 'DELIVERED', label: 'Delivered', icon: PackageCheck },
    { value: 'CANCELLED', label: 'Cancelled', icon: CircleX },
];

const STATUS_PILL_CLASS = {
    PENDING: styles.statusPending,
    DELIVERED: styles.statusDelivered,
    CANCELLED: styles.statusCancelled,
};

// Amount with Indian digit grouping, for display only
const formatAmount = (value) =>
    Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const OrderView = () => {
    const [status, setStatus] = useState('PENDING');
    const [orders, setOrders] = useState([]);
    const [expandedOrders, setExpandedOrders] = useState({});
    // UI only: shows a spinner instead of an empty table while orders load
    const [loading, setLoading] = useState(false);

    const fetchOrders = () => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        const storeId = user?.storeId; // Retrieve storeId from user data

        setLoading(true);
        fetch(`${API_BASE_URL}/user/view/customer_order_details?status=${status}&storeId=${storeId}`, {
            method: 'POST',
        })
            .then(response => response.json())
            .then(data => {
                console.log('Fetched data:', data);
                setOrders(data);
            })
            .catch(error => console.error('Error fetching orders:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchOrders();
    }, [status]);

    const toggleExpandOrder = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const formatValue = (value) => value || 'N/A';

    const updateOrderStatus = (orderId, statusType) => {
        const url = statusType === 'DELIVERED'
            ? `${API_BASE_URL}/user/update/customer_order_details/delivered?orderId=${orderId}`
            : `${API_BASE_URL}/user/update/customer_order_details/cancelled?orderId=${orderId}`;

        fetch(url, {
            method: 'POST'
        })
        .then(response => {
            if (response.ok) {
                setOrders(prevOrders => prevOrders.filter(order => order.orderId !== orderId));
            } else {
                console.error('Failed to update order status');
            }
        })
        .catch(error => console.error('Error updating order status:', error));
    };

    const orderList = Array.isArray(orders) ? orders : [];
    const activeTab = STATUS_TABS.find(tab => tab.value === status) || STATUS_TABS[0];
    const ActiveIcon = activeTab.icon;
    const sumBy = (key) => orderList.reduce((sum, order) => sum + (Number(order[key]) || 0), 0);
    const totalAmount = sumBy('totalAmount');
    const advanceTotal = sumBy('advancePayment');
    const dueTotal = sumBy('amount_due');

    const summaryCards = [
        { key: 'total', label: 'Total amount', value: `₹${formatAmount(totalAmount)}`, icon: IndianRupee, primary: true },
        { key: 'advance', label: 'Advance received', value: `₹${formatAmount(advanceTotal)}`, icon: Wallet },
        { key: 'due', label: 'Amount due', value: `₹${formatAmount(dueTotal)}`, icon: Receipt },
        { key: 'count', label: 'Orders', value: orderList.length, icon: ClipboardList },
    ];

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.titleBlock}>
                    <span className={styles.headerIcon} aria-hidden="true">
                        <BookOpen size={20} />
                    </span>
                    <h1 className={styles.title}>Order Book</h1>
                    <div className={styles.chips}>
                        <span className={styles.chip}>
                            <ActiveIcon size={13} aria-hidden="true" />
                            {activeTab.label}
                        </span>
                        <span className={styles.chip}>
                            {orderList.length} {orderList.length === 1 ? 'order' : 'orders'}
                        </span>
                    </div>
                </div>

                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btnGhost}`}
                        onClick={fetchOrders}
                        disabled={loading}
                    >
                        <RefreshCw size={15} className={loading ? styles.spin : undefined} aria-hidden="true" />
                        Refresh
                    </button>
                </div>
            </header>

            {/* -------------------- Summary cards -------------------- */}
            <div className={styles.summaryGrid}>
                {summaryCards.map(({ key, label, value, icon: Icon, primary }) => (
                    <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
                        <span className={styles.summaryIcon} aria-hidden="true">
                            <Icon size={18} />
                        </span>
                        <div className={styles.summaryText}>
                            <span className={styles.summaryLabel}>{label}</span>
                            <span className={styles.summaryValue}>{value}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* -------------------- Results card -------------------- */}
            <div className={styles.card}>
                <div className={styles.toolbar}>
                    <div className={styles.segmented} role="tablist" aria-label="Order status">
                        {STATUS_TABS.map(({ value, label, icon: Icon }) => (
                            <button
                                key={value}
                                type="button"
                                role="tab"
                                aria-selected={status === value}
                                className={`${styles.segBtn} ${status === value ? styles.segBtnOn : ''}`}
                                onClick={() => setStatus(value)}
                            >
                                <Icon size={14} aria-hidden="true" />
                                {label}
                            </button>
                        ))}
                    </div>
                    <span className={styles.resultCount}>
                        {orderList.length} {activeTab.label.toLowerCase()} {orderList.length === 1 ? 'order' : 'orders'}
                    </span>
                </div>

                <div className={styles.tableWrap}>
                    {loading && (
                        <div className={styles.loadingOverlay}>
                            <span className={styles.spinner} aria-hidden="true" />
                            <span className={styles.loadingText}>Loading orders…</span>
                        </div>
                    )}

                    {orderList.length > 0 ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Customer Name</th>
                                    <th>Mobile No</th>
                                    <th>Delivery Date</th>
                                    <th className={styles.num}>Total Amount</th>
                                    <th className={styles.num}>Advance Payment</th>
                                    <th className={styles.num}>Amount Due</th>
                                    <th>Status</th>
                                    <th className={styles.alignCenter}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderList.map(order => {
                                    const isOpen = !!expandedOrders[order.orderId];
                                    const items = order.orders || [];
                                    return (
                                        <React.Fragment key={order.orderId}>
                                            <tr className={isOpen ? styles.rowOpen : undefined}>
                                                <td className={styles.strong}>{order.orderId}</td>
                                                <td className={styles.strong}>{formatValue(order.customerName)}</td>
                                                <td>{formatValue(order.mobileNo)}</td>
                                                <td>{new Date(order.deliveryDate).toLocaleDateString()}</td>
                                                <td className={styles.num}>{formatAmount(order.totalAmount)}</td>
                                                <td className={styles.num}>{formatAmount(order.advancePayment)}</td>
                                                <td className={`${styles.num} ${styles.strong}`}>{formatAmount(order.amount_due)}</td>
                                                <td>
                                                    <span className={`${styles.statusPill} ${STATUS_PILL_CLASS[order.status] || ''}`}>
                                                        {formatValue(order.status)}
                                                    </span>
                                                </td>
                                                <td className={styles.alignCenter}>
                                                    <div className={styles.rowActions}>
                                                        <button
                                                            type="button"
                                                            className={`${styles.btn} ${styles.btnSm} ${styles.btnSuccess}`}
                                                            onClick={() => updateOrderStatus(order.orderId, 'DELIVERED')}
                                                        >
                                                            <PackageCheck size={14} aria-hidden="true" />
                                                            Delivered
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
                                                            onClick={() => updateOrderStatus(order.orderId, 'CANCELLED')}
                                                        >
                                                            <CircleX size={14} aria-hidden="true" />
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`${styles.btn} ${styles.btnSm} ${styles.btnGhost} ${isOpen ? styles.btnGhostOn : ''}`}
                                                            onClick={() => toggleExpandOrder(order.orderId)}
                                                            aria-expanded={isOpen}
                                                        >
                                                            {isOpen ? 'Collapse' : 'View'}
                                                            <ChevronDown
                                                                size={14}
                                                                className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr className={styles.subRow}>
                                                    <td colSpan="9">
                                                        <div className={styles.subPanel}>
                                                            {items.length > 0 ? (
                                                                <table className={styles.subTable}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>School Name</th>
                                                                            <th>Item Type</th>
                                                                            <th>Description</th>
                                                                            <th>Size</th>
                                                                            <th className={styles.num}>Quantity</th>
                                                                            <th className={styles.num}>Price</th>
                                                                            <th className={styles.num}>Amount</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {items.map((orderItem, index) => (
                                                                            <tr key={index}>
                                                                                <td>{formatValue(orderItem.schoolName)}</td>
                                                                                <td>{formatValue(orderItem.itemType)}</td>
                                                                                <td>{formatValue(orderItem.description)}</td>
                                                                                <td>{formatValue(orderItem.size)}</td>
                                                                                <td className={styles.num}>{orderItem.quantity}</td>
                                                                                <td className={styles.num}>{formatAmount(orderItem.price)}</td>
                                                                                <td className={`${styles.num} ${styles.strong}`}>{formatAmount(orderItem.amount)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <div className={styles.subEmpty}>No items on this order.</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="4">Total</td>
                                    <td className={styles.num}>{formatAmount(totalAmount)}</td>
                                    <td className={styles.num}>{formatAmount(advanceTotal)}</td>
                                    <td className={styles.num}>{formatAmount(dueTotal)}</td>
                                    <td colSpan="2" />
                                </tr>
                            </tfoot>
                        </table>
                    ) : (
                        !loading && (
                            <div className={styles.noResults}>
                                <span className={styles.noResultsIcon} aria-hidden="true">
                                    <Inbox size={28} />
                                </span>
                                <p className={styles.noResultsTitle}>No {activeTab.label.toLowerCase()} orders</p>
                                <p className={styles.noResultsText}>
                                    There are no orders with this status right now. Try another tab or refresh.
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderView;
